import React, {
  Fragment,
  PropsWithChildren,
  ReactNode,
} from 'react';
import clsx from 'clsx';
import WalkthroughIcon from 'views/EMR/components/PCC/Walkthrough/icons';
import type {
  IconKey,
  ProcessStepMarker,
} from 'views/EMR/components/PCC/Walkthrough/types';

type Part = 'root' | 'icon' | 'title' | 'description';

type IconContainerProps = {
  iconKey: IconKey;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'pale' | 'solid' | 'none';
  className?: string;
};

export function IconContainer({
  iconKey,
  size = 'md',
  variant = 'pale',
  className,
}: IconContainerProps) {
  const sizeClasses = {
    sm: 'h-[26px] w-[26px] rounded-[13px]',
    md: 'h-11 w-11 rounded-xl',
    lg: 'h-16 w-16 rounded-full',
  }[size];

  const iconSizeClasses = {
    sm: '!h-4 !w-4',
    md: '!h-[22px] !w-[22px]',
    lg: '!h-[34px] !w-[34px]',
  }[size];

  const variantClasses = {
    pale: 'bg-brand-100 text-brand-500',
    solid: 'bg-brand-500 text-white',
    none: 'bg-transparent text-brand-500',
  }[variant];

  return (
    <div
      className={clsx(
        'flex shrink-0 items-center justify-center',
        sizeClasses,
        variantClasses,
        className,
      )}
    >
      <WalkthroughIcon
        iconKey={iconKey}
        className={iconSizeClasses}
      />
    </div>
  );
}

type InformationCardProps = {
  title: string;
  description: string;
  iconKey?: IconKey;
  classes?: Partial<Record<Part, string>>;
  children?: ReactNode;
};

export function InformationCard({
  title,
  description,
  iconKey,
  classes,
  children,
}: InformationCardProps) {
  return (
    <div
      className={clsx(
        'flex flex-col gap-2 rounded-[14px] border border-[#E2E8F0] bg-white p-4 sm:p-[18px]',
        classes?.root,
      )}
    >
      {iconKey ? (
        <IconContainer
          iconKey={iconKey}
          size="md"
          className={clsx('!h-10 !w-10 !rounded-[10px]', classes?.icon)}
        />
      ) : null}
      <div className={clsx('text-[15px] font-bold text-[#0F1B2D]', classes?.title)}>
        {title}
      </div>
      <div
        className={clsx(
          'text-[13px] leading-[1.45] text-[#475569]',
          classes?.description,
        )}
      >
        {description}
      </div>
      {children}
    </div>
  );
}

type BenefitCardProps = {
  title: string;
  description: string;
  iconKey: IconKey;
};

export function BenefitCard({
  title,
  description,
  iconKey,
}: BenefitCardProps) {
  return (
    <div className="flex flex-1 basis-0 flex-col gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-6 text-left">
      <IconContainer iconKey={iconKey} />
      <div className="text-lg font-bold text-[#0F1B2D]">
        {title}
      </div>
      <div className="text-[15px] leading-normal text-[#475569]">
        {description}
      </div>
    </div>
  );
}

type TitleDescriptionItemProps = {
  title: string;
  description: string;
  iconKey?: IconKey;
  iconVariant?: 'pale' | 'solid' | 'none';
  classes?: Partial<Record<Part, string>>;
};

export function TitleDescriptionItem({
  title,
  description,
  iconKey,
  iconVariant = 'pale',
  classes,
}: TitleDescriptionItemProps) {
  return (
    <div className={clsx('flex items-start gap-3', classes?.root)}>
      {iconKey ? (
        <IconContainer
          iconKey={iconKey}
          size={iconVariant === 'none' ? 'sm' : 'sm'}
          variant={iconVariant}
          className={clsx(
            iconVariant === 'none' && '!h-[22px] !w-[22px]',
            classes?.icon,
          )}
        />
      ) : null}
      <div className="flex flex-col gap-0.5">
        <div className={clsx('text-[15px] font-semibold text-[#0F1B2D]', classes?.title)}>
          {title}
        </div>
        <div
          className={clsx(
            'text-sm leading-[1.45] text-[#475569]',
            classes?.description,
          )}
        >
          {description}
        </div>
      </div>
    </div>
  );
}

type NumberedProcessItemProps = {
  number: number;
  title: string;
  description: string;
  showConnector?: boolean;
  variant?: 'process' | 'card';
  marker?: ProcessStepMarker;
};

type ProcessStepMarkerProps = {
  number: number;
  marker: ProcessStepMarker;
  size: 'sm' | 'md';
};

function ProcessStepMarkerBadge({
  number,
  marker,
  size,
}: ProcessStepMarkerProps) {
  const sizeClasses = size === 'sm'
    ? 'h-8 w-8 text-base'
    : 'h-9 w-9 text-base';
  const markerClasses = {
    number: 'bg-brand-500 text-white',
    completed: 'bg-brand-500 text-white',
    upcoming: 'border border-[#CBD5E1] bg-white text-[#64748B]',
  }[marker];

  return (
    <div
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full font-bold',
        sizeClasses,
        markerClasses,
      )}
    >
      {marker === 'completed' ? (
        <WalkthroughIcon
          iconKey="check"
          className="!h-4 !w-4"
        />
      ) : number}
    </div>
  );
}

export function NumberedProcessItem({
  number,
  title,
  description,
  showConnector = false,
  variant = 'process',
  marker = 'number',
}: NumberedProcessItemProps) {
  return variant === 'card' ? (
    <div className="flex flex-1 basis-0 flex-col gap-2.5 rounded-[14px] border border-[#E2E8F0] bg-white p-[22px]">
      <ProcessStepMarkerBadge
        number={number}
        marker={marker}
        size="sm"
      />
      <div className="text-base font-bold text-[#0F1B2D]">
        {title}
      </div>
      <div className="text-sm leading-normal text-[#475569]">
        {description}
      </div>
    </div>
  ) : (
    <div className="flex flex-1 basis-0 flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <ProcessStepMarkerBadge
          number={number}
          marker={marker}
          size="md"
        />
        {showConnector ? (
          <div className="hidden h-0.5 flex-1 bg-[#E2E8F0] lg:block" />
        ) : null}
      </div>
      <div className="text-[17px] font-bold text-[#0F1B2D]">
        {title}
      </div>
      <div className="text-sm leading-[1.55] text-[#475569]">
        {description}
      </div>
    </div>
  );
}

type CalloutProps = {
  text: string;
  iconKey: IconKey;
  variant?: 'brand' | 'caution';
};

export function Callout({
  text,
  iconKey,
  variant = 'brand',
}: CalloutProps) {
  return (
    <div
      className={clsx(
        'flex items-start gap-3 rounded-xl px-5 py-4 text-[15px] leading-normal text-[#0F1B2D]',
        {
          'bg-brand-100': variant === 'brand',
          'bg-[#FFF7E8]': variant === 'caution',
        },
      )}
    >
      <WalkthroughIcon
        iconKey={iconKey}
        className={clsx(
          '!h-5 !w-5 shrink-0',
          variant === 'brand' ? '!text-brand-700' : '!text-[#7A4A00]',
        )}
      />
      <span>{text}</span>
    </div>
  );
}

type CautionPanelProps = {
  sections: Array<{
    title: string;
    description: string;
    iconKey: IconKey;
  }>;
};

export function CautionPanel({ sections }: CautionPanelProps) {
  return (
    <div className="flex flex-col gap-4 rounded-xl bg-[#FFF7E8] px-6 py-[22px]">
      {sections.map((section, index) => (
        <Fragment key={section.title}>
          {index > 0 ? <div className="h-px bg-[#F1DDB8]" /> : null}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-base font-bold text-[#7A4A00]">
              <WalkthroughIcon
                iconKey={section.iconKey}
                className="!h-5 !w-5 !text-[#7A4A00]"
              />
              {section.title}
            </div>
            <div className="pl-7 text-sm leading-[1.55] text-[#0F1B2D]">
              {section.description}
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

type PrimaryButtonProps = PropsWithChildren<{
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  showArrow?: boolean;
}>;

export function PrimaryButton({
  children,
  onClick,
  disabled = false,
  className,
  showArrow = false,
}: PrimaryButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'inline-flex h-[52px] items-center justify-center gap-2.5 rounded-full px-7 text-base font-bold text-white transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        disabled
          ? 'cursor-not-allowed bg-[#E2E8F0] text-[#4F5A57]'
          : 'bg-brand-700 hover:bg-brand-800',
        className,
      )}
    >
      {children}
      {showArrow && !disabled ? (
        <WalkthroughIcon
          iconKey="arrowRight"
          className="!h-[18px] !w-[18px] !text-white"
        />
      ) : null}
    </button>
  );
}

type SecondaryButtonProps = PropsWithChildren<{
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}>;

export function SecondaryButton({
  children,
  onClick,
  disabled = false,
  className,
}: SecondaryButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'inline-flex h-[54px] items-center rounded-full border border-[#E2E8F0] px-[26px] text-base font-semibold text-[#0F1B2D] transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:bg-white/80',
        className,
      )}
    >
      {children}
    </button>
  );
}

type TextLinkButtonProps = PropsWithChildren<{
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}>;

export function TextLinkButton({
  children,
  onClick,
  disabled = false,
  className,
}: TextLinkButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'text-[15px] font-medium text-brand-500 transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:text-brand-700',
        className,
      )}
    >
      {children}
    </button>
  );
}

type ExternalLinkProps = {
  href: string;
  label: string;
  className?: string;
};

export function ExternalLink({
  href,
  label,
  className,
}: ExternalLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={clsx(
        'text-[15px] font-semibold text-brand-700 hover:text-brand-800',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        className,
      )}
    >
      {label}
    </a>
  );
}

type UnavailableLinkProps = {
  label: string;
  helperText: string;
  className?: string;
};

export function UnavailableLink({
  label,
  helperText,
  className,
}: UnavailableLinkProps) {
  return (
    <span
      className={clsx(
        'inline-flex flex-col gap-1 text-[15px] font-semibold text-[#94A3B8]',
        className,
      )}
      aria-disabled="true"
    >
      <span>{label}</span>
      <span className="text-xs font-medium text-[#64748B]">
        {helperText}
      </span>
    </span>
  );
}
